import React, { useState, useEffect } from 'react';
import { IDocument } from '@cyntler/react-doc-viewer';

import {
  Box,
  Button,
  Dialog,
  Divider,
  DialogTitle,
  DialogActions,
  DialogContent,
} from '@mui/material';

import useLocales from '../../hooks/useLocales';
import { getFileType } from '../../utils/getFileFormat';
import DocumentsViewer from '../../utils/documents/DocumentsViewer';

// ----------------------------------------------------------------------

export type DocumentViewerFile = {
  uri: string;
  fileName: string;
  fileType?: string;
};

type DocumentViewerDialogProps = {
  open: boolean;
  onClose: () => void;
  file?: DocumentViewerFile | null;
  docs?: IDocument[];
  title?: string;
  children?: React.ReactNode;
};

const DocumentViewerDialog: React.FC<DocumentViewerDialogProps> = ({
  open,
  onClose,
  file,
  docs: externalDocs,
  title,
  children,
}) => {
  const { t } = useLocales();

  const [internalDocs, setInternalDocs] = useState<IDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const docs = externalDocs || internalDocs;

  useEffect(() => {
    if (file && !externalDocs) {
      setInternalDocs([
        {
          uri: file.uri,
          fileType: file.fileType || getFileType(file.fileName),
          fileData: file.uri,
          fileName: file.fileName,
        },
      ]);
    }
  }, [file, externalDocs]);

  useEffect(() => {
    if (docs.length > 0) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [docs]);

  const dialogTitle = title || file?.fileName || t('documents.preview');

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>{dialogTitle}</DialogTitle>

      <DialogContent>
        {children}

        <Box
          sx={{
            mt: children ? 2 : 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <DocumentsViewer
            docs={docs}
            isLoading={isLoading}
            setIsLoading={() => {}}
            showNavigation={false}
            isZooming
            setIsZooming={() => {}}
          />
        </Box>
      </DialogContent>

      <Box sx={{ width: '100%', mb: 2 }}>
        <Divider />
      </Box>

      <DialogActions sx={{ display: 'flex', justifyContent: 'left', textAlign: 'center', gap: 1 }}>
        <Button variant="outlined" onClick={onClose} id="doc-viewer-close-btn">
          {t('shared.buttons.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentViewerDialog;
